<template>
  <Teleport to="body">
    <transition name="backdrop-fade" @after-enter="afterEnter">
      <div
        v-if="visible"
        ref="modal"
        class="modal-overlay"
        :class="[activeModal]"
        tabindex="0"
        @click="$emit('close-modal')"
        @keydown.esc="$emit('close-modal')"
      >
        <transition :name="modalTransitionName">
          <div v-if="showContent" class="modal" @click.stop>
            <div class="promo-modal-modal">
              <DynamicImage
                loading="eager"
                :renditions="eagerRenditions"
                :image="state.image"
                priority="high"
                fit="cover"
                class="promo-modal-image"
              />
              <div class="promo-modal-content">
                <div class="promo-modal-heading">
                  <span v-if="state.prefix" class="sub-title">{{ state.prefix }}</span>
                  <Heading :level="2" :content="state.title" class="title" />
                </div>
                <span v-if="state.description" class="message">
                  {{ state.description }}
                </span>

                <div v-if="state.buttonUrl" class="promo-modal-button">
                  <Button
                    type="cta"
                    :label="state.buttonText"
                    :to="state.buttonUrl"
                    target="_blank"
                    icon="eenvandaag-general:external-2"
                    icon-position="right"
                  />
                </div>
              </div>
            </div>
          </div>
        </transition>
        <button class="modal-close" @click="$emit('close-modal')">
          <Icon name="eenvandaag-general:x-close" />
        </button>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { IImageRendition } from '~~/types/local_types';
import type { ICmsMessageModals } from '~~/types/_cms-types';

const showContent = ref(false);
const modal = ref<HTMLElement | null>(null);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  backdropTransitionName: {
    type: String,
    default: 'backdrop-fade',
  },
  modalTransitionName: {
    type: String,
    default: 'modal-fade',
  },
  data: {
    type: Object as () => ICmsMessageModals,
    default: null,
  },
});

const state = reactive({
  title: props.data.title || 'EenVandaag Whatsapp-kanaal',
  prefix: props.data.prefix || 'EenVandaag Whatsapp-kanaal',
  description:
    props.data.description ||
    'Volg ons Whatsapp-kanaal om de laatste nieuws te ontvangen en te ontdekken wat er nieuw is in EenVandaag.',
  image: props.data.image || {
    url: '/',
    alt: 'EenVandaag Whatsapp-kanaal',
  },
  buttonText: props.data.buttonText || 'Volg ons Whatsapp-kanaal',
  buttonUrl: props.data.buttonUrl || null,
});

const reactiveVisible = toRef(props, 'visible'); // Stays reactive!

defineEmits(['close-modal']);

// Watch for changes in the visible prop and set the showContent value to false
// This is needed to reset the showContent value to false when the modal is closed
watch(
  () => props.visible,
  (visible) => {
    bodyScrollLock(visible);
    if (!visible) {
      showContent.value = false;
    }
  },
);

const afterEnter = (): void => {
  showContent.value = true;
  // focus the modal when it is opened so that the user can use the keyboard to navigate / close the modal
  modal.value?.focus();
};

const activeModal = showContent.value ? 'modal-is--enabled' : null;

const eagerRenditions: IImageRendition[] = [
  { breakpoint: 'sm', size: 160, aspectRatio: '3x2' },
  { breakpoint: 'md', size: 1024, aspectRatio: '3x2' },
];

useFocusTrap(modal, reactiveVisible);
</script>

<style lang="postcss" scoped>
.modal-overlay {
  --modal-padding-top-bottom: 10vh;
  --modal-padding-left-right: var(--unit-10);

  --modal-max-width: 512px;

  position: fixed;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(0deg, rgba(0, 10, 69, 0.8) 0.03%, rgba(0, 8, 54, 0.8) 99.97%);
  backdrop-filter: blur(16px);
  z-index: 9999;
  padding: var(--modal-padding-top-bottom) var(--modal-padding-left-right);
  touch-action: none;
  justify-content: center;
  top: 0;

  &:focus {
    outline: none;
  }

  &.modal-is--enabled {
    touch-action: auto;
  }

  @media (--sm-viewport) {
    @media screen and (orientation: landscape) {
      padding: var(--modal-padding-top-bottom) var(--modal-padding-left-right);
    }
  }
}

.modal-close {
  --modal-close-size: var(--unit-24);
  --modal-close-color: hsl(var(--color-neutral-white));
  --modal-close-position: var(--unit-18);

  position: absolute;
  display: flex;
  font-size: var(--modal-close-size);
  top: var(--modal-close-position);
  right: var(--modal-close-position);
  color: var(--modal-close-color);
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  transition: color var(--modal-close-icon-duration) ease;

  @media (--lg-viewport) {
    --modal-close-size: var(--unit-28);
    --modal-close-position: var(--unit-32);
  }
}

.modal {
  text-align: center;
  height: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: var(--modal-max-width);
  color: hsl(var(--color-neutral-white));
  overflow: auto;
  border: 2px solid hsl(var(--color-primary-700));

  @media screen and (orientation: landscape) {
    max-height: calc(100vh - (var(--modal-padding-top-bottom) / 2));
  }
}

.backdrop-fade-enter-active,
.backdrop-fade-enter,
.backdrop-fade-leave-active {
  transition: opacity 300ms ease;
}

.backdrop-fade-leave-to,
.backdrop-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-active,
.modal-fade-enter,
.modal-fade-leave-active {
  transition: opacity 300ms ease;
}

.modal-fade-leave-to,
.modal-fade-enter-from {
  opacity: 0;
}

/* IN YOUR FACE MODAL */

.promo-modal-image {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;

  &::before {
    position: absolute;
    bottom: 0;
    content: '';
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      180deg,
      hsla(var(--color-primary-950), 0) 0%,
      hsla(var(--color-primary-950), 0.3) 25%,
      hsla(var(--color-primary-950), 0.5) 50%,
      hsl(var(--color-primary-950)) 100%
    );
  }
}

.promo-modal-modal {
  position: relative;
  background-color: hsl(var(--color-primary-950));
}

.sub-title {
  @add-mixin heading-xs;
  color: hsl(var(--color-primary-200));
}

.title {
  @add-mixin heading-lg;
  color: hsl(var(--color-neutral-white));
}

.message {
  @add-mixin body-md;
  color: hsl(var(--color-primary-50));
}

.promo-modal-heading {
  --heading-gap: var(--unit-6);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  gap: var(--heading-gap);
}

.promo-modal-content {
  --content-padding: 274px var(--unit-32) var(--unit-32);
  --content-gap: var(--unit-16);
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  padding: var(--content-padding);
  gap: var(--content-gap);

  @media (--lg-viewport) {
    --content-gap: var(--unit-20);
    --content-padding: 277px var(--unit-48) var(--unit-48);
  }
}
.promo-modal-button {
  --button-margin: var(--unit-8) 0 0 0;

  margin: var(--button-margin);

  @media (--lg-viewport) {
    --button-margin: var(--unit-12) 0 var(--unit-12);
  }
}
</style>
